import { Component, input, Input, OnDestroy, signal } from '@angular/core';

import { Store } from '@ngrx/store';

import { UpdatePointCustomField } from 'src/app/project/modules/points/points.actions';
import { TPoint } from 'src/app/project/modules/points/points.model';

import { SitePointFilterService } from 'src/app/project/modules/filters/site-point-filter.service';
import { PointsUpdateService } from 'src/app/project/modules/points/points-update.service';
import { PromptService } from '../../../../../components/prompt/prompt.service';
import { PointActivityService } from '../../point-timeline/point-activity.service';
import { PointFieldsService } from '../point-fields.service';

import { TPointUpdate } from '@project/view-models';
import { of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { TPointCustomField } from 'src/app/project/view-models/custom-field-response-model';
import { EIconPath } from '../../../../../shared/enums/icons.enum';

@Component({
    selector: 'pp-point-fields-checkbox',
    templateUrl: './point-fields-checkbox.component.html',
    styleUrls: ['./point-fields-checkbox.component.scss', '../point-fields.component.scss'],
    standalone: false
})
export class PointFieldsCheckboxComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  @Input() ppWorkspaceId: string;
  @Input() ppPointId: string;
  @Input() ppFieldId: string;
  @Input() ppFieldLabel: string;
  @Input() ppField: TPointCustomField;
  @Input() ppNew: boolean;
  @Input() ppCanEdit: boolean;
  @Input() ppValue = 'false';
  @Input() ppFieldDescription: string;
  @Input() ppIsLocked = false;
  updating = false;
  EIconPath = EIconPath;

  constructor(
    private store: Store<{ points: TPoint[] }>,
    private promptService: PromptService,
    private pointFieldsService: PointFieldsService,
    private pointActivityService: PointActivityService,
    private sitePointFilterService: SitePointFilterService,
    private pointsUpdateService: PointsUpdateService,
    private translationPipe: TranslationPipe,
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
  }

  clearCustomField(): void {
    this.ppValue = null;

    this.updateField();
  }

  private updateField(): void {
    const _id = this.ppPointId;
    const fieldId = this.ppFieldId;
    const fieldValue = this.ppValue;
    const workspaceId = this.ppWorkspaceId;

    if (this.ppNew) {
      this.updateFieldForNewPoint(this.ppPointId, this.ppFieldId, this.ppValue, this.ppWorkspaceId);

      return;
    }

    this.updating = true;

    const body: TPointUpdate = {
      customFieldsList: [
        {
          customFieldTemplateId: fieldId,
          value: fieldValue,
        },
      ],
    };

    this.pointsUpdateService
      .updatePointField(_id, body)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          const promptText = this.translationPipe.transform('prompt_point_checkbox_update');

          this.promptService.showSuccess(promptText);
          this.pointActivityService.refreshTimeline(workspaceId, _id);
          this.sitePointFilterService.filterPoints({ _keepScrollPosition: true });
        }),
        catchError((error) => {
          this.pointFieldsService.showUpdatePointFieldError(error);

          return of();
        }),
        finalize(() => {
          this.updating = false;
        }),
      )
      .subscribe();
  }

  toggleField(): void {
    if (!this.updating) {
      this.ppValue = this.ppValue === 'true' ? 'false' : 'true';

      this.updateField();
    }
  }

  private updateFieldForNewPoint(
    pointId: string,
    fieldId: string,
    value: string,
    workspaceId: string,
  ): void {
    this.store.dispatch(
      new UpdatePointCustomField({
        workspaceId: workspaceId,
        pointId: pointId,
        customFieldId: fieldId,
        customFieldValue: value,
      }),
    );
  }
}
